import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigationType } from 'react-router-dom';
import { Megaphone, X } from 'lucide-react';
import { useBrand } from '../brand/BrandProvider';
import { useWorkspace } from '../workspace/WorkspaceProvider';
import { useWorkspaceKind } from '../workspace/useWorkspaceKind';
import { shouldShowWelcome } from '../firstrun/welcome';
import { useFocusTrap } from '../lib/useFocusTrap';
import { inAppPath } from '../lib/inAppPath';
import { btns } from '../components/buttons';
import type { Announcement } from '../api/types';
import { useVisibleAnnouncements } from './useAnnouncements';
import { AnnouncementBody } from './markdown';
import { formatDisplayDate, isForYourClients, substituteProduct } from './unread';
import {
  markPopupDismissed, markPopupShownThisSession, noModalOpen, popupCandidate, popupShownThisSession,
} from './popup';

/**
 * The announcement popup (docs/42 §6, steward-app#2613), mounted in `Layout`.
 *
 * WHEN. The candidate rules live in `popupCandidate`. The moment is decided HERE, at the instant
 * it would open, and only when the location changes (or the candidate first arrives): an active
 * workspace, the welcome overlay done or not applicable (`shouldShowWelcome` false: a firm
 * workspace or a visiting advisor never sees that overlay, so never gets `welcomeSeenAt`), the
 * assistant panel closed, and no `aria-modal="true"` in the document. A failed check is retried
 * on the next navigation in the same session, never on a timer, and never because a blocker went
 * away (a welcome card closing is not a quiet moment). At most one per browser session
 * (`sessionStorage`), set when it opens. Overlays that are not `aria-modal` (the name and landing
 * nudges) do not block it.
 *
 * WHILE OPEN. Another `aria-modal` appearing (idle warning, step-up, a dialog) makes the popup
 * step aside without recording a dismissal, so the item returns next session; the same happens
 * on a workspace switch. A redirect (a REPLACE, such as a firm's `/` to `/advisor`) keeps it open
 * and moves its baseline. Leaving the page it opened on (a link in its body, Back) closes it AND
 * records the dismissal, as the buttons do.
 *
 * WHAT IT DOES NOT TOUCH. Opening or closing never writes `announcementsSeenAt`: only visiting
 * the page clears the nav badge. Every close writes `announcementPopupDismissed`.
 */
export function AnnouncementPopup({ assistantOpen }: { assistantOpen: boolean }) {
  const { pathname } = useLocation();
  const navigationType = useNavigationType();
  const { isWhiteLabel } = useBrand();
  const { activeWorkspaceId, activeMembership, profile, setPreferences } = useWorkspace();
  const workspaceKind = useWorkspaceKind();
  const { items, viewerAudience, settled } = useVisibleAnnouncements();
  const dismissedMap = profile?.preferences?.announcementPopupDismissed;
  const welcomeSeen = !!profile && !shouldShowWelcome({
    profile, workspaceId: activeWorkspaceId, membership: activeMembership, workspaceKind,
  });

  // Items from useVisibleAnnouncements are already branded-host filtered and sorted; passing
  // isWhiteLabel again is a no-op that keeps `popupCandidate` safe to call on raw items.
  const candidate = settled && activeWorkspaceId
    ? popupCandidate(items, { isWhiteLabel, viewerAudience, dismissed: dismissedMap, now: Date.now() })
    : null;

  const [shown, setShown] = useState<Announcement | null>(null);
  // Where and in which workspace it opened, so leaving either closes it.
  const openedAt = useRef<{ pathname: string; workspaceId: string | null } | null>(null);

  // The latest values, read when the effect below fires. They are deliberately not its
  // dependencies: a blocker clearing must not open the popup by itself.
  const latest = useRef({ candidate, welcomeSeen, assistantOpen, activeWorkspaceId, shown });
  latest.current = { candidate, welcomeSeen, assistantOpen, activeWorkspaceId, shown };

  // Ids closed in this mount, so a blocked sessionStorage cannot reopen the item just closed.
  const closedIds = useRef(new Set<string>());
  const candidateId = candidate?.id ?? null;
  useEffect(() => {
    const l = latest.current;
    if (l.shown || !l.candidate || closedIds.current.has(l.candidate.id)) return;
    if (popupShownThisSession()) return;
    if (!l.activeWorkspaceId || !l.welcomeSeen || l.assistantOpen || !noModalOpen()) return;
    markPopupShownThisSession();
    openedAt.current = { pathname, workspaceId: l.activeWorkspaceId };
    setShown(l.candidate);
  }, [pathname, candidateId]);

  const trapRef = useFocusTrap<HTMLDivElement>(shown !== null);

  /** Hide without recording a dismissal: the item returns next session. */
  const stepAside = (item: Announcement) => {
    closedIds.current.add(item.id);
    setShown(null);
  };

  const close = (item: Announcement) => {
    stepAside(item);
    void setPreferences({
      announcementPopupDismissed: markPopupDismissed(dismissedMap, item.id, new Date().toISOString()),
    });
  };

  // Leaving the page or the workspace it opened in. No dependency array: `close` captures the
  // current map, and the checks are cheap.
  useEffect(() => {
    const at = openedAt.current;
    if (!shown || !at) return;
    if (activeWorkspaceId !== at.workspaceId) stepAside(shown);
    else if (pathname !== at.pathname) {
      // A redirect is not the person leaving: keep it open on the page they land on.
      if (navigationType === 'REPLACE') at.pathname = pathname;
      else close(shown);
    }
  });

  // Another modal opening on top: step aside rather than cover it.
  useEffect(() => {
    if (!shown) return undefined;
    const own = trapRef.current;
    const observer = new MutationObserver(() => {
      const other = Array.from(document.querySelectorAll('[aria-modal="true"]')).some((el) => el !== own);
      if (other) stepAside(shown);
    });
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-modal'] });
    return () => observer.disconnect();
  }, [shown]); // eslint-disable-line react-hooks/exhaustive-deps

  // Escape closes, unless something else already handled it. No dependency array, as
  // WelcomeOverlay: `close` captures the current map.
  useEffect(() => {
    if (!shown) return undefined;
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape' && !e.defaultPrevented) close(shown); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  });

  if (!shown) return null;

  // A family-only item shown in a firm workspace would point at a family route, so no link.
  const path = isForYourClients(shown, viewerAudience) ? null : inAppPath(shown.link);
  const title = substituteProduct(shown.title);

  return createPortal((
    <div
      className="fixed inset-0 z-[70] flex items-end justify-center bg-navy/45 md:items-center md:px-4"
      data-testid="announcement-popup-scrim"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="announcement-popup-title"
        data-testid="announcement-popup"
        ref={trapRef}
        className="popup-enter relative box-border max-h-[90vh] w-full overflow-y-auto rounded-t-[6px] bg-surface px-5 pb-7 pt-3 shadow-panel md:max-w-[480px] md:rounded-token md:px-9 md:pb-7 md:pt-9"
      >
        <div aria-hidden className="mx-auto mb-4 h-1 w-10 rounded-full bg-line md:hidden" />
        <button
          type="button"
          aria-label="Close"
          onClick={() => close(shown)}
          className="absolute right-3 top-2 flex h-11 w-11 cursor-pointer items-center justify-center border-none bg-transparent text-slate md:right-3.5 md:top-3.5"
        >
          <X size={18} strokeWidth={1.75} aria-hidden />
        </button>
        <div className="mb-3.5 flex items-center gap-2.5 pr-10">
          <Megaphone size={16} strokeWidth={1.75} aria-hidden className="hidden text-bronze-text md:inline-flex" />
          <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-bronze-text">Announcement</span>
          <time className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate" dateTime={shown.displayDate}>
            {formatDisplayDate(shown.displayDate)}
          </time>
        </div>
        <h2
          id="announcement-popup-title"
          className="m-0 mb-3 font-serif text-[22px] font-normal leading-tight text-navy md:text-[24px]"
        >
          {title}
        </h2>
        <div className="mb-6 text-[14px] leading-[1.65] text-body md:mb-7">
          <AnnouncementBody body={shown.body} />
        </div>
        <div className="flex flex-col gap-2.5 md:flex-row md:items-center md:gap-3">
          {path && (
            <Link to={path} onClick={() => close(shown)} className={`${btns.btnPrim} no-underline text-center md:px-5 md:py-2.5`}>
              Take a look
            </Link>
          )}
          <button type="button" onClick={() => close(shown)} className={`${btns.btn} md:px-5 md:py-2.5`}>
            Close
          </button>
        </div>
        <div className="mt-5 border-t border-line pt-4 text-[13px] md:mt-[22px]">
          <Link
            to="/announcements"
            onClick={() => close(shown)}
            className="font-semibold text-bronze-text underline-offset-[3px]"
          >
            See all announcements
          </Link>
        </div>
      </div>
    </div>
  ), document.body);
}
